<template>
  <b-card>
    <layout-loading v-if="itemLoading" />
    <b-form v-if="render">
      <b-row>
        <b-col md="4">
          <b-form-group
            :label="$t('general.firstName')"
            label-for="manager-firstName"
          >
            <b-form-input
              id="manager-firstName"
              v-model="form.firstName"
              :state="validateState('firstName')"
            />
            <b-form-invalid
              id="input-firstName-feedback"
              :label="$t('general.firstName')"
              :validator="$v.form.firstName"
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group
            :label="$t('general.lastName')"
            label-for="manager-lastName"
          >
            <b-form-input
              id="manager-lastName"
              v-model="form.lastName"
              :state="validateState('lastName')"
            />
            <b-form-invalid
              id="input-lastName-feedback"
              :label="$t('general.lastName')"
              :validator="$v.form.lastName"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('managers.isCommentable')"
            label-for="manager-isCommentable"
            class="mb-2"
          >
            <b-form-checkbox
              id="manager-isCommentable"
              v-model="form.isCommentable"
              class="mt-1"
              name="manager-isCommentable"
              switch
              inline
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.showOnHome')"
            label-for="manager-showOnHome"
            class="mb-2"
          >
            <b-form-checkbox
              id="manager-showOnHome"
              v-model="form.showOnHome"
              class="mt-1"
              name="manager-showOnHome"
              switch
              inline
            />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group
            :label="$t('general.priority')"
            label-for="manager-sort"
          >
            <b-form-input
              id="manager-sort"
              v-model="form.sort"
            />
          </b-form-group>
        </b-col>
        <b-col md="8">
          <b-form-group
            :label="$t('managers.speciality')"
            label-for="manager-speciality"
          >
            <b-form-input
              id="manager-speciality"
              v-model="form.speciality"
              :state="validateState('speciality')"
            />
            <b-form-invalid
              id="input-speciality-feedback"
              :label="$t('managers.speciality')"
              :validator="$v.form.speciality"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.slug')"
            label-for="manager-slug"
          >
            <b-form-input
              id="manager-slug"
              v-model="form.slug"
              :state="validateState('slug')"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('general.slug')"
              :validator="$v.form.slug"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.language')"
            label-for="manager-lang"
          >
            <language-selector
              v-model="form.lang"
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.categories')"
            label-for="manager-category"
            :state="validateState('categories')"
          >
            <v-select-custom
              id="manager-category"
              v-model="form.categories"
              :custom-props="{ multiple: true }"
              :options="categoryOptions"
            />
            <b-form-invalid
              id="input-category-feedback"
              :label="$t('general.category')"
              :validator="$v.form.categories"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.isActive')"
            label-for="manager-isActive"
            class="mb-2"
          >
            <b-form-checkbox
              id="manager-isActive"
              v-model="form.isActive"
              class="mt-1"
              name="manager-isActive"
              switch
              inline
            />
          </b-form-group>
        </b-col>
      </b-row>
      <template v-if="form.lang !== 'ar'">
        <b-row class="mb-2">
          <b-col>
            <b-button
              variant="outline-primary"
              @click="addBookingItem"
            >
              {{ $t("general.addBookingItem") }}
            </b-button>
          </b-col>
        </b-row>
        <b-row
          v-for="(booking, index) in form.bookings"
          :key="index"
          class="mt-1 bg-booking p-1"
        >
          <b-col md="3">
            <b-form-group
              :label="$t('general.clinicName')"
              label-for="manager-clinic"
            >
              <v-select-custom
                v-model="booking.clinic"
                :options="clinicOptions"
                @input="fetchDoctorsByClinicId($event)"
              />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group
              :label="$t('general.doctor')"
              label-for="manager-doctor"
            >
              <v-select-custom
                v-model="booking.doctor"
                :options="doctorOptions"
              />
            </b-form-group>
          </b-col>
          <b-col class="mt-2">
            <b-button
              variant="outline-success"
              @click="removeBookingItem(index)"
            >
              {{ $t("general.removeBookingItem") }}
            </b-button>
          </b-col>
        </b-row>
      </template>
      <b-row>
        <b-col md="12">
          <b-form-group
            :label="$t('general.keywords')"
            label-for="manager-keywords"
          >
            <b-form-input
              id="manager-keywords"
              v-model="form.keywords"
              :state="validateState('keywords')"
            />
            <b-form-invalid
              id="input-keywords-feedback"
              :label="$t('general.keywords')"
              :validator="$v.form.keywords"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.description')"
            label-for="manager-description"
          >
            <b-form-textarea
              id="manager-description"
              v-model="form.description"
              :state="validateState('description')"
              rows="6"
              aria-describedby="input-description-feedback"
            />
            <b-form-invalid
              id="input-description-feedback"
              :label="$t('general.description')"
              :validator="$v.form.description"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group
            label-for="manager-content"
            class="mb-2"
          >
            <CkeditorBasic
              id="manager-content"
              v-model="form.content"
              :label="$t('general.content')"
              :state="validateState('content')"
            />
            <b-form-invalid
              id="input-content-feedback"
              :label="$t('general.content')"
              :validator="$v.form.content"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-file-input
          id="managerImage"
          v-model="form.managerImage"
          :label="$t('general.image') + ' ' + $t('general.acceptImage')"
          :state="validateState('managerImage')"
          :alt.sync="form.managerImageAlt"
          :alt-validator="$v.form.managerImageAlt"
        >
          <template #invalidFeedback>
            <b-form-invalid
              id="input-manager-image-feedback"
              :label="$t('general.image')"
              :validator="$v.form.managerImage"
            />
          </template>
        </b-file-input>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.submit") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>
<script>
import {
  BButton, BCard, BCol, BForm, BFormCheckbox, BFormGroup, BFormInput, BFormTextarea, BRow,
} from 'bootstrap-vue'
import BFileInput from '@/components/BFileInput/index.vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import CkeditorBasic from '@/components/CkeditorBasic/index.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import Ripple from 'vue-ripple-directive'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import '@/components/VSelectCustom/VSelectOption.vue'
import languageSelector from '@/components/language-selector.vue'
import { required, minLength } from 'vuelidate/lib/validators'
import useManagersList from './useManagersList'

export default {
  name: 'ManagerEdit',
  components: {
    BButton,
    BCard,
    BCol,
    BFileInput,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormTextarea,
    BRow,
    BFormCheckbox,
    CkeditorBasic,
    LayoutLoading,
    VSelectCustom,
    languageSelector,
  },
  directives: {
    Ripple,
  },
  validations() {
    return {
      form: {
        firstName: {
          required,
          minLength: minLength(3),
        },
        lastName: {
          required,
        },
        speciality: {
          required,
          minLength: minLength(3),
        },
        slug: {
          required,
          isUnique: () => this.isUniqueSlug,
        },
        categories: {
          required,
        },
        keywords: {
          required,
          minLength: minLength(3),
        },
        description: {
          required,
          minLength: minLength(10),
        },
        content: {
          required,
          minLength: minLength(10),
        },
        managerImage: {
          required,
        },
        managerImageAlt: {
          required,
          minLength: minLength(3),
        },
      },
    }
  },
  setup() {
    const {
      addItem,
      categoryOptions,
      checkSlugIsUnique,
      clinicOptions,
      convertResource,
      doctorOptions,
      errorManager,
      fetchCategories,
      fetchClinics,
      fetchDoctorsByClinicId,
      fetchItem,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      item,
      itemLoading,
      updateItem,
    } = useManagersList()
    return {
      addItem,
      categoryOptions,
      checkSlugIsUnique,
      clinicOptions,
      convertResource,
      doctorOptions,
      errorManager,
      fetchCategories,
      fetchClinics,
      fetchDoctorsByClinicId,
      fetchItem,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      item,
      itemLoading,
      updateItem,
    }
  },
  computed: {
    itemId() {
      return this.$router.currentRoute.params.id
    },
    render() {
      return this.item ? this.item && Object.keys(this.item).length : true
    },
  },
  watch: {
    'form.slug': function () {
      this.checkSlugIsUnique(this.form.lang, this.form.slug)
    },
    'form.isActive': function (newValue) {
      if (typeof newValue !== 'boolean') {
        this.$set(this.form, 'isActive', newValue === true)
      }
    },
  },
  created() {
    this.fetchClinics(this.form.lang)
    this.fetchItem(this.itemId).then(() => {
      this.fetchCategories(this.form.lang)
    })
  },
  methods: {
    addBookingItem() {
      if (this.form.bookings.length === 0) {
        this.form.bookings.push({
          clinic: '',
          doctor: '',
        })
      } else if (this.form.bookings[this.form.bookings.length - 1].clinic !== '') {
        this.form.bookings.push({
          clinic: '',
          doctor: '',
        })
      }
    },
    removeBookingItem(index) {
      this.form.bookings.splice(index, 1)
    },
    changeSelectedLanguage(selectedLanguage) {
      this.form = this.convertResource(this.item, selectedLanguage)
      this.fetchCategories(selectedLanguage)
    },
    validateState(name) {
      const {
        $dirty,
        $error,
      } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    submitForm() {
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        }), 500)
        return
      }
      const uploadList = ['managerImage']
      this.handleUpload(this.form, uploadList).then(newForm => {
        this.sendData(newForm)
      })
    },
    sendData(form) {
      const data = {
        firstName: form.firstName,
        lastName: form.lastName,
        speciality: form.speciality,
        showOnHome: this.form.showOnHome,
        sort: this.form.sort,
        slug: form.slug,
        isCommentable: this.form.isCommentable != null ? this.form.isCommentable : false,
        isActive: !!this.form.isActive,
        keywords: form.keywords,
        description: form.description,
        content: form.content,
        managerImageFileId: form.managerImage && form.managerImage._id,
        managerCategoryIds: form.categories.map(item => item._id),
        booking: this.form.bookings ? this.form.bookings.map(item => ({
          clinic: item.clinic,
          doctor: item.doctor,
        })) : [],
      }
      this.updateItem(this.itemId, data, this.form.lang)
    },
  },
}
</script>

<style>
.bg-booking{
  background-color: #f6fafb;
}
</style>
